/**
 * FEATURED curation — which cartridges lead the gallery (sc-8136).
 *
 * Three tiers, in order of preference:
 *
 *   1. LIVE      — GET /v1/cartridges/featured, fetched at boot. Editable
 *                  from the admin console, so the front run changes with no
 *                  deploy.
 *   2. CACHED    — the last good response, in localStorage, so a returning
 *                  player gets the curated order on the very first frame
 *                  instead of after the network settles.
 *   3. BUILT-IN  — catalog.ts `FEATURED_ORDER`. The gallery is an installed
 *                  PWA and has to render offline, so there is ALWAYS a
 *                  sensible answer even on a cold cache with no network.
 *
 * The built-in list is a DEFAULT, not a mirror of the database. The two are
 * allowed to diverge; the server wins the moment it answers. Same shape as
 * plays.ts, which does cache-then-refresh for global play counts.
 */

import { getFeaturedCartridges } from '../api/client.js';
import { CATALOG, FEATURED_ORDER } from '../catalog.js';

const CACHE_KEY = 'arcadia-remastered-featured';

/** Ids in running order, or null when nothing has been resolved yet. */
let resolved: string[] | null = readCache();

function readCache(): string[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const ids = parsed.filter((v): v is string => typeof v === 'string');
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}

function writeCache(ids: string[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(ids));
  } catch {
    /* private-mode Safari — curation just re-fetches next boot */
  }
}

/**
 * Ids that should lead the gallery, in order.
 *
 * Always filtered against CATALOG: the database can name a cartridge this
 * build does not ship — one gated to `stage: 'dev'`, or removed entirely —
 * and rendering it would produce a card that 404s on launch.
 */
export function featuredIds(): readonly string[] {
  const known = new Set(CATALOG.map((e) => e.id));
  const source = resolved ?? FEATURED_ORDER;
  const usable = source.filter((id) => known.has(id));
  // An empty result means the server curated nothing we can show. Fall back
  // rather than render an empty run.
  return usable.length > 0 ? usable : FEATURED_ORDER.filter((id) => known.has(id));
}

/**
 * Fetch the live curation. Resolves true when the run CHANGED and the
 * caller should re-render; false when unreachable or already current.
 */
export async function refreshFeatured(): Promise<boolean> {
  try {
    const { featured } = await getFeaturedCartridges();
    const ids = [...featured].sort((a, b) => a.rank - b.rank).map((f) => f.cartridgeId);
    // An empty server response is a legitimate "nothing is curated" — cache
    // it so the fallback is used consistently, but there is nothing to show.
    const before = featuredIds().join(',');
    resolved = ids;
    writeCache(ids);
    return featuredIds().join(',') !== before;
  } catch {
    return false;
  }
}
